/**
 * Thematic raster layers — spec §F5.2 (`layersOn`) + §D5 (`tile_template` / `wms`) +
 * §F5.1 (`rasterPane`, z350: under the choropleth, the boundaries and the vectors).
 *
 * One `<RasterLayer>` per switched-on raster; each owns its `L.TileLayer` and removes it
 * in the effect's cleanup, so switching off, changing role (the catalogue re-fetches) or
 * leaving the map leaves nothing on the canvas.
 *
 * Three kinds reach Leaflet differently:
 *   - `cog_titiler` — the §D5 template **verbatim**. Leaflet substitutes `{z}/{x}/{y}` and
 *     sends the rest of the query byte-for-byte, which is the contract the §I2 nginx
 *     allow-list depends on: it rejects any `%` inside `/tiles/`, so encoding the `,` of
 *     `rescale` or the `/` of the COG path turns a working tile into a 403 (Task 2, NOTES
 *     #247). Never wrap the template in `encodeURI`.
 *   - `wms_external` with `params` — `L.tileLayer.wms` against the §B3 proxy URL, with the
 *     server's `params` spread in.
 *   - `wms_external` with `wms.template` — a **RESTful WMTS** upstream (NASA GIBS), whose
 *     coordinates live in the path. `L.tileLayer(url + template)`, because `L.tileLayer.wms`
 *     would append a GetMap query the server has no route for.
 *
 * **§1a rule 4 (docs/DATA_SOURCES.md).** A `display_only` row is live third-party imagery
 * that is never stored, so it cannot be pre-clipped to the programme. Two controls stand in:
 * the caching proxy refuses tiles that do not intersect the programme bbox
 * (`apps.datasets.wms_bounds`), and `useProgrammeClip` masks what is left to the programme
 * outline. Both are needed — the proxy works in whole tiles, and one z6 tile touching
 * Burkina also covers most of Ghana.
 *
 * Missing pixels are silent. Most seeded `cog_titiler` rows still point at a COG no
 * harvester has written yet (only `soc`, `ph` and `chirps` are published), so TiTiler
 * answers 404/500 for them; `errorTileUrl` swaps in a transparent 1×1 rather than letting
 * Leaflet paint its broken-image glyph across the Sahel. The `unavailable` rows never get
 * this far — §F5.2 disables their checkbox. A display-only tile refused by the §1a gate is
 * the same story: a 403 is a transparent pixel, not a broken image.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';

import { fetchLayers, fetchProgrammeExtent, queryKeys, type Layer } from '../../lib/api';
import { DEFAULT_LAYER_OPACITY, useAppStore } from '../../lib/state';
import { drawnEpochOf, isDateSeries, NDVI_SERIES } from './epochs';
import { isRasterLayer, useRasterRegistry } from './layerKinds';
import { useMap } from './mapContext';
import { MAX_ZOOM } from './panes';
import { useProgrammeClip, type LonLatRing } from './ProgrammeClip';
import { useScopeClip } from './ScopeClip';

/**
 * The crop-mask stencil's z-index within `rasterPane` — any value above the NDVI tile it
 * sits over is correct (neither ever sets its own, so both would otherwise land on
 * Leaflet's shared default), a round number well clear of it is just legible in a diff.
 */
const STENCIL_Z_INDEX = 500;

/** 1×1 fully transparent PNG — the "this tile does not exist" pixel. */
export const TRANSPARENT_TILE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

function RasterLayer({ layer, rings }: { layer: Layer; rings?: LonLatRing[] }) {
  const map = useMap();
  const opacity = useAppStore((s) => s.layerOpacity[layer.id] ?? DEFAULT_LAYER_OPACITY);
  const layersOn = useAppStore((s) => s.layersOn);
  const epochOn = useAppStore((s) => s.epochOn);
  // State, not a ref: `useProgrammeClip` has to re-run when the layer is (re)created, and a
  // ref mutation does not re-render. One `setState` per layer creation, not per tile.
  const [tileLayer, setTileLayer] = useState<L.TileLayer | null>(null);

  // Task 6: for a **date series** (`ndvi_terra`, `swi`, `cm_synth`), which epoch is on
  // screen is `drawnEpochOf` (`epochOn[layer.id]`, defaulting to the newest) — every other
  // layer is unaffected, `epoch` stays `null` and the fields below fall through to the
  // layer's own `tile_template`/`wms.template`, exactly as before Task 6.
  const epoch = isDateSeries(layer) ? drawnEpochOf(layer, layersOn, epochOn) : null;
  const epochDrawnDate = epoch?.date ?? null;
  const epochDrawnKey = epoch?.key ?? null;

  // Creation depends on the *URL*, not on the opacity: a slider drag must not tear the
  // tile layer down and re-request every tile. Opacity is pushed in the second effect.
  const template = epoch?.tile_template ?? layer.tile_template;
  const wmsUrl = layer.wms?.url;
  const wmsParams = layer.wms?.params;
  // A proxied date series (`swi`, `cm_synth`) substitutes its drawn epoch's own token into
  // the upstream's `{date}` placeholder — the epoch's `key` when the upstream spells it
  // differently from `date` (Crop Monitor's `2025_05`), else `date` itself (GLAM's SWI).
  // Every other `wms_external` row's template has no `{date}` to replace, so this is a
  // no-op for them.
  const rawWmsTemplate = layer.wms?.template;
  // F3: a template declaring `{date}` with no epoch to substitute it (the series' `epochs`
  // came back empty, so `epoch` above is `null`) must render nothing — asking Leaflet to
  // fetch a URL with `{date}` still in it throws "No value provided for variable {date}"
  // rather than failing quietly the way a missing tile does.
  const wmsTemplateNeedsDate = rawWmsTemplate?.includes('{date}') ?? false;
  const wmsTemplateUnresolved = wmsTemplateNeedsDate && !epoch;
  const wmsTemplate =
    epoch && rawWmsTemplate
      ? rawWmsTemplate.replace('{date}', epoch.key ?? epoch.date ?? '')
      : wmsTemplateUnresolved
        ? undefined
        : rawWmsTemplate;
  const wmsMaxZoom = layer.wms?.max_zoom;
  const attribution = layer.attribution ?? undefined;

  useEffect(() => {
    let tile: L.TileLayer | null = null;
    const common = {
      pane: 'rasterPane',
      // The programme COGs are ~1 km (Task 2, NOTES #241); past z12 TiTiler would be asked
      // to upsample a pixel the source does not have. Leaflet stretches the z12 tile
      // instead — same picture, a quarter of the requests. An external upstream says how
      // deep it goes in `wms.max_zoom` (GIBS' matrix set stops at 9), and past that a
      // request is a 404 per tile rather than more detail.
      maxNativeZoom: wmsMaxZoom ?? MAX_ZOOM,
      tileSize: 256,
      errorTileUrl: TRANSPARENT_TILE,
      crossOrigin: true as const,
      // A CC-BY condition, not decoration: Leaflet renders it in the map's attribution
      // control for as long as the layer is on, and drops it when the layer is removed.
      ...(attribution ? { attribution } : {}),
    };
    if (layer.kind === 'cog_titiler' && template) {
      tile = L.tileLayer(template, common);
    } else if (layer.kind === 'wms_external' && wmsUrl && !wmsTemplateUnresolved) {
      tile = wmsTemplate
        ? L.tileLayer(`${wmsUrl}${wmsTemplate}`, common)
        : L.tileLayer.wms(wmsUrl, {
            format: 'image/png',
            transparent: true,
            ...(wmsParams ?? {}),
            ...common,
          });
    }
    if (!tile) return;
    tile.addTo(map);
    setTileLayer(tile);
    return () => {
      map.removeLayer(tile);
      setTileLayer(null);
    };
  }, [
    map,
    layer.kind,
    template,
    wmsUrl,
    wmsParams,
    wmsTemplate,
    wmsTemplateUnresolved,
    wmsMaxZoom,
    attribution,
    // Task 6: a date series' tile URL is otherwise unchanged when the *reader* picks a
    // different epoch of it — `template`/`wmsTemplate` already reflect the new epoch above,
    // but naming the epoch's own fields here documents why re-creating the tile layer on an
    // epoch change is intentional, not incidental to some other prop.
    epochDrawnDate,
    epochDrawnKey,
  ]);

  useEffect(() => {
    tileLayer?.setOpacity(opacity);
  }, [tileLayer, opacity]);

  // §1a rule 4 — the programme-outline mask, on this layer's own container so it composes
  // with §G6's AOI clip and swipe instead of fighting them for `pane.style.clipPath`.
  useProgrammeClip(map, tileLayer, rings, layer.display_only);

  return null;
}

/**
 * Task 6: the crop-mask overlay (`mask_maize`, GEOGLAM-BACS) stencilled above a drawn NDVI
 * series. `template` is the mask row's own `stencil_template` — a plain TiTiler `{z}/{x}/{y}`
 * URL like any `cog_titiler` row's, so it draws the same way `RasterLayer` does, just without
 * that component's opacity slider, programme clip or epoch machinery: a mask is not itself a
 * switchable layer, it rides on whichever NDVI series is on.
 *
 * `zIndex` alone separates it from the NDVI tile underneath, since both sit in the same
 * `rasterPane` (§F5.1) — the pane decides the raster/choropleth/vector stack, not which
 * raster sits on top of another raster in the same pane.
 */
function StencilLayer({ map, template }: { map: L.Map; template: string }) {
  useEffect(() => {
    const tile = L.tileLayer(template, {
      pane: 'rasterPane',
      tileSize: 256,
      errorTileUrl: TRANSPARENT_TILE,
      opacity: 1,
      zIndex: STENCIL_Z_INDEX,
      crossOrigin: true,
    });
    tile.addTo(map);
    return () => {
      map.removeLayer(tile);
    };
  }, [map, template]);

  return null;
}

/**
 * UC2's land-cover comparison (`lib/state.ts`'s `swipePair`): the two published COGs the
 * swipe handle sits between — `right` into `rasterPane`, which `Swipe.tsx` already clips to
 * the right of the handle, `left` into `swipeLeftPane`, which the same handle clips the
 * other way.
 *
 * Deliberately **not** routed through `layersOn`: §F5.2's "one raster at a time" register
 * would refuse the second one, and the reader's own raster pick has nothing to do with a
 * comparison a use-case chip opened. So these two are drawn straight from the catalogue
 * row's `tile_template` and removed again the moment the pair is cleared, leaving the
 * Layers pane exactly as it was.
 *
 * A pair naming a row this catalogue does not have (or one with no template — an
 * unpublished COG) simply draws nothing on that side: the chip is still usable, with the
 * basemap as the other half.
 */
function SwipePairLayer({ layer, pane }: { layer: Layer; pane: string }) {
  const map = useMap();
  const template = layer.tile_template;

  useEffect(() => {
    if (!template) return;
    const tile = L.tileLayer(template, {
      pane,
      tileSize: 256,
      maxNativeZoom: MAX_ZOOM,
      errorTileUrl: TRANSPARENT_TILE,
      crossOrigin: true,
      attribution: layer.attribution ?? undefined,
    });
    tile.addTo(map);
    return () => {
      map.removeLayer(tile);
    };
  }, [map, template, pane, layer.attribution]);

  return null;
}

export function RasterLayers() {
  const map = useMap();
  const role = useAppStore((s) => s.role);
  const layersOn = useAppStore((s) => s.layersOn);
  const ndviMask = useAppStore((s) => s.ndviMask);
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });

  // Client review R4: this component is mounted for the whole life of the map and already
  // holds the catalogue, so it is where the store learns which ids are rasters.
  useRasterRegistry(layers);

  const drawn = (layers ?? []).filter((l) => layersOn.has(l.id) && isRasterLayer(l));
  const swipePair = useAppStore((s) => s.swipePair);
  const pairLeft = swipePair ? layers?.find((l) => l.id === swipePair.left) : undefined;
  const pairRight = swipePair ? layers?.find((l) => l.id === swipePair.right) : undefined;
  const needsOutline = drawn.some((l) => l.display_only);

  // Client round 4 E4: while the §F5.8 filter is on, the raster pane is cut to the scoped
  // unit's outline. Driven from here because "is a raster actually drawn" is this component's
  // own answer — see `ScopeClip.ts`, which owns the geometry, the precedence and the events.
  useScopeClip(map, drawn.length > 0);

  // Fetched only when a display-only layer is actually on: the outline is 13 kB of geometry
  // that a reader who never switches one on has no use for. `staleTime` is generous because
  // the extent moves only when a boundary version is approved (`/meta/programme-extent` is
  // served with `max-age=3600` for the same reason).
  const { data: extent } = useQuery({
    queryKey: queryKeys.programmeExtent(),
    queryFn: fetchProgrammeExtent,
    enabled: needsOutline,
    staleTime: 60 * 60 * 1000,
  });

  // Task 6: the crop-mask stencil draws only when all three hold — a mask is picked, the
  // catalogue actually has that row *with* a `stencil_template` (a hand-edited permalink can
  // name anything), and the raster it would sit above is actually an NDVI series right now.
  // A mask left picked while the reader switches to `soc` must not silently reappear the
  // moment they switch back to `ndvi_aqua` if that was never the series they picked it for —
  // but nothing here distinguishes which NDVI series it was, matching `setActiveRaster`'s own
  // rule that any non-NDVI raster clears `ndviMask` outright.
  const ndviDrawn = drawn.some((l) => (NDVI_SERIES as readonly string[]).includes(l.id));
  const maskLayer = ndviMask ? layers?.find((l) => l.id === ndviMask) : undefined;
  const stencilTemplate = ndviDrawn ? maskLayer?.stencil_template : undefined;

  return (
    <>
      {drawn.map((l) => (
        <RasterLayer key={l.id} layer={l} rings={extent?.rings as LonLatRing[] | undefined} />
      ))}
      {stencilTemplate && <StencilLayer map={map} template={stencilTemplate} />}
      {pairLeft && <SwipePairLayer layer={pairLeft} pane="swipeLeftPane" />}
      {pairRight && <SwipePairLayer layer={pairRight} pane="rasterPane" />}
    </>
  );
}

export default RasterLayers;
